import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    linkedSignal,
    resource,
    Signal,
    signal,
    untracked,
    WritableSignal
} from '@angular/core';
import { httpResource, HttpResourceRef } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DepartmentService } from 'src/app/services/department.service';
import { QueueStatusGroup } from '../synchronization.model';
import {
    CountsArray,
    DEFAULT_QUERY_FILTERS,
    MonitoringCountsMap,
    MonitoringScopeDto,
    MonitoringTemplateDto,
    QueueExecuterDto,
    QueueQueryFilters,
    QueueRequestDetailDto,
    QueueRequestListItemDto,
    QueueRequestRow,
    QueuesResponseDto,
    QueueTimeWindow,
    QueueUpdatesResponseDto
} from './queue-request.model';
import { QueueCountsQuery, QueueRequestService, QueueServerQuery } from './queue-request.service';
import {
    isTerminal,
    narrowestWindowFor,
    payloadFileName,
    progressOf,
    windowContains
} from './queue-request.util';
import { QueueRequestFiltersComponent } from './queue-request-filters/queue-request-filters.component';
import { QueueRequestListComponent } from './queue-request-list/queue-request-list.component';
import { QueueRequestDetailComponent } from './queue-request-detail/queue-request-detail.component';
import { parseHttpError } from 'src/app/common/errors/app-error.model';

const PAGE_SIZE = 200;
const POLL_MIN_MS = 10_000;
const POLL_MAX_MS = 120_000;
const POLL_FACTOR = 2;
/** After this many consecutive /updates failures the loop stops rather than hammering a broken API. */
const POLL_FAILURE_LIMIT = 3;
/** Long enough that a typed word is one round trip, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 350;

/** A page or update patch, stamped with the query key it was fetched under. */
interface QueuePagePatch {
    key: string;
    append: boolean;
    items: QueueRequestListItemDto[];
    hasMore: boolean;
    anchorIncluded: boolean;
}

interface QueueUpdatePatch {
    key: string;
    items: QueueRequestListItemDto[];
}

/**
 * Rows only. The badge counts deliberately do <b>not</b> live here — a filter change resets this
 * store, and when the counts were part of it that reset blanked every badge to 0 on every click.
 */
interface QueueListStore {
    key: string;
    items: readonly QueueRequestListItemDto[];
    hasMore: boolean;
    /** Raw ISO of the newest known row — the cursor the poll asks "changed since?" with. */
    newest: string | null;
    anchorIncluded: boolean;
    lastPage: QueuePagePatch | null | undefined;
    lastUpdate: QueueUpdatePatch | null | undefined;
}

function emptyStore(key: string): QueueListStore {
    return {
        key,
        items: [],
        hasMore: false,
        newest: null,
        anchorIncluded: false,
        lastPage: undefined,
        lastUpdate: undefined
    };
}

function newestOf(items: readonly QueueRequestListItemDto[]): string | null {
    return items.reduce<string | null>(
        (newest, item) => (newest === null || item.lastActivity > newest ? item.lastActivity : newest),
        null
    );
}

function byActivityDesc(a: QueueRequestListItemDto, b: QueueRequestListItemDto): number {
    if (a.lastActivity === b.lastActivity) return a.id < b.id ? 1 : -1;
    return a.lastActivity < b.lastActivity ? 1 : -1;
}

function shallowEqualFilters(a: QueueQueryFilters, b: QueueQueryFilters): boolean {
    return a.templateId === b.templateId
        && a.search === b.search
        && a.window === b.window
        && a.day === b.day
        && a.statusGroup === b.statusGroup;
}

/** Index into a counts array: `[all, running, finished, warning, failed]`. */
const STATUS_INDEX: Record<QueueStatusGroup, number> = {
    all: 0, running: 1, finished: 2, warning: 3, failed: 4
};

/**
 * Value equality for the two query computeds.
 *
 * Load-bearing, not tidiness. A computed returning an object literal is a new identity on every
 * recompute, and `Object.is` then reports a change even when nothing changed — so a status click
 * would refetch the counts (blanking the badges, the exact bug this design removes) and every
 * keystroke would refetch the rows straight past the debounce.
 */
function sameCountsQuery(a: QueueCountsQuery | null, b: QueueCountsQuery | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.departmentId === b.departmentId
        && a.customerId === b.customerId
        && a.synchronizationId === b.synchronizationId
        && a.templateId === b.templateId
        && a.search === b.search
        && a.day === b.day;
}

function sameServerQuery(a: QueueServerQuery | null, b: QueueServerQuery | null): boolean {
    return sameCountsQuery(a, b) && a?.window === b?.window && a?.statusGroup === b?.statusGroup;
}

/**
 * The two-pane kørsel browser: filters + list on the left, detail accordion on the right.
 *
 * Embedded by both the Overvågning page (department-wide, or narrowed by its dropdowns) and a
 * synchronization's detail page (one synchronization, fixed). Filtering for sync A on Overvågning
 * and opening sync A directly are the same view by construction, because they are the same
 * component with different inputs.
 *
 * It owns all the reactivity — resources, store, cursor, selection, polling and the URL query
 * params — and measures nothing: each host is responsible for giving it a height.
 */
@Component({
    selector: 'app-queue-request-browser',
    templateUrl: './queue-request-browser.component.html',
    styleUrl: './queue-request-browser.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [QueueRequestFiltersComponent, QueueRequestListComponent, QueueRequestDetailComponent]
})
export class QueueRequestBrowserComponent {
    readonly customerId = input<number | null>(null);
    readonly synchronizationId = input<string | null>(null);

    private readonly api = inject(QueueRequestService);
    private readonly departmentService = inject(DepartmentService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly toastr = inject(ToastrService);

    private readonly departmentId = computed(() => this.departmentService.selectedDepartment()?.id ?? null);

    /**
     * Resets more than a filter change does: a new scope invalidates the selection and the template
     * choice too, not just the page of rows.
     */
    private readonly scopeKey = computed(() =>
        `${this.departmentId() ?? ''}|${this.customerId() ?? ''}|${this.synchronizationId() ?? ''}`
    );

    //#region Filters

    private readonly initialParams = this.route.snapshot.queryParams;
    private readonly initialQueueId: string | null = this.initialParams['queueId'] ?? null;

    private readonly initialQuery: QueueQueryFilters = {
        templateId: this.initialParams['templateId'] ?? null,
        search: this.initialParams['search'] ?? DEFAULT_QUERY_FILTERS.search,
        window: (this.initialParams['time'] as QueueTimeWindow) ?? DEFAULT_QUERY_FILTERS.window,
        day: this.initialParams['day'] ?? null,
        statusGroup: (this.initialParams['status'] as QueueStatusGroup) ?? DEFAULT_QUERY_FILTERS.statusGroup
    };

    /**
     * User-writable, with two resets — hence a linkedSignal rather than a signal plus effects:
     * a scope change clears the template (the window and status group are preferences and survive),
     * and the deep-link hint only ever *widens* the window, so a URL asking for 7d is never narrowed
     * to 1h just because its kørsel is recent.
     *
     * `equal` matters: re-clicking the active window button yields a structurally identical object,
     * which would otherwise read as a change and refetch.
     *
     * The explicit type annotation breaks a type-level inference cycle (filters → hint → detail →
     * selection → store → list → filters); without it the build fails with TS7022. There is no
     * runtime cycle — a resource's `value()` is state written by its loader. Same reason for `store`,
     * `selectedId` and `detailResource` below.
     */
    protected readonly queryFilters: WritableSignal<QueueQueryFilters> = linkedSignal<
        { hint: QueueTimeWindow | null; scopeKey: string },
        QueueQueryFilters
    >({
        source: () => ({ hint: this.windowHint(), scopeKey: this.scopeKey() }),
        computation: (source, previous) => {
            if (previous === undefined) return this.initialQuery;

            let next = previous.value;
            if (source.scopeKey !== previous.source.scopeKey && next.templateId !== null) {
                next = { ...next, templateId: null };
            }
            if (source.hint && !windowContains(next.window, source.hint)) {
                next = { ...next, window: source.hint, day: null };
            }
            return next;
        },
        equal: shallowEqualFilters
    });

    /**
     * `queryFilters().search` trails behind by a debounce before it reaches either resource key.
     *
     * The typed value stays in `queryFilters` so the input and the URL update immediately; only the
     * two resources wait. Both wait on the *same* signal, so the rows and the badges always refetch
     * for the same term — never one term apart.
     */
    private readonly debouncedSearch = signal(this.initialQuery.search);

    //#endregion
    //#region Names and Templates

    private readonly scopeResource = httpResource<MonitoringScopeDto>(() => {
        const departmentId = this.departmentId();
        if (!departmentId) return undefined;
        return {
            url: this.api.scopeUrl(departmentId, this.synchronizationId()),
            method: 'GET',
            ...this.api.requestOptions
        };
    });

    protected readonly templatesResource = httpResource<MonitoringTemplateDto[]>(() => {
        const departmentId = this.departmentId();
        if (!departmentId) return undefined;
        return { url: this.api.templatesUrl(departmentId), method: 'GET', ...this.api.requestOptions };
    });

    /**
     * synchronizationId → the three names a row shows.
     *
     * The queue endpoints carry ids only: customer names live in the shared DB and company names per
     * tenant, so joining them there would mean a tenant fan-out on every poll. Resolving them once
     * from /scope is what keeps the polled endpoint single-database.
     */
    private readonly namesBySync = computed(() => {
        const index = new Map<string, { customerName: string; companyName: string; synchronizationName: string }>();
        for (const customer of this.scopeResource.value()?.customers ?? []) {
            for (const sync of customer.synchronizations) {
                index.set(sync.id, {
                    customerName: customer.customerName,
                    companyName: sync.companyName,
                    synchronizationName: sync.name
                });
            }
        }
        return index;
    });

    //#endregion
    //#region Server Query

    /**
     * Everything above the time and status buttons: scope, template, search, day.
     *
     * This — and deliberately not the window or the status group — is what the counts key on. The
     * counts response covers all four windows × all five groups, so those two controls change the
     * badges by re-reading it, which is why clicking them cannot blank a badge or cost a request.
     */
    private readonly countsQuery = computed<QueueCountsQuery | null>(() => {
        const departmentId = this.departmentId();
        const synchronizationId = this.synchronizationId();

        // Idle until there is a scope. A synchronization id is specific enough on its own; anything
        // else needs the ambient department, which arrives asynchronously.
        if (!departmentId && !synchronizationId) return null;

        const filters = this.queryFilters();
        return {
            departmentId,
            customerId: this.customerId(),
            synchronizationId,
            templateId: filters.templateId,
            search: this.debouncedSearch(),
            day: filters.day
        };
    }, { equal: sameCountsQuery });

    private readonly serverQuery = computed<QueueServerQuery | null>(() => {
        const counts = this.countsQuery();
        if (!counts) return null;

        const filters = this.queryFilters();
        return { ...counts, window: filters.window, statusGroup: filters.statusGroup };
    }, { equal: sameServerQuery });

    private readonly queryKey = computed(() => JSON.stringify(this.serverQuery()));

    /** Reset by any key change — which is most of the stale-scope protection, for free. */
    private readonly cursor = linkedSignal<string, string | null>({
        source: () => this.queryKey(),
        computation: () => null
    });

    /** Only the URL-supplied id is anchored, and only until the user clicks something. */
    private readonly anchorId = signal<string | null>(this.initialQueueId);

    //#endregion
    //#region Resources

    /**
     * resource(), not httpResource: the loader has to stamp the result with the key it was fetched
     * under. httpResource's parse never sees the params, and that stamp is what stops a slow
     * response from a previous scope landing in the new scope's store.
     */
    protected readonly listResource = resource<QueuePagePatch | null, {
        key: string;
        query: QueueServerQuery | null;
        cursor: string | null;
        anchor: string | null;
    }>({
        params: () => ({
            key: this.queryKey(),
            query: this.serverQuery(),
            cursor: this.cursor(),
            anchor: this.anchorId()
        }),
        loader: async ({ params, abortSignal }) => {
            if (!params.query) return null;
            const response: QueuesResponseDto = await this.api.getQueues(
                params.query,
                {
                    take: PAGE_SIZE,
                    beforeTimestamp: params.cursor ?? undefined,
                    anchorQueueRequestId: params.anchor ?? undefined
                },
                abortSignal
            );
            return {
                key: params.key,
                append: params.cursor !== null,
                items: response.items,
                hasMore: response.hasMore,
                anchorIncluded: response.anchorIncluded
            };
        }
    });

    protected readonly updatesResource = resource<QueueUpdatePatch | null, {
        key: string;
        query: QueueServerQuery | null;
    }>({
        params: () => ({ key: this.queryKey(), query: this.serverQuery() }),
        loader: async ({ params, abortSignal }) => {
            // Read untracked: making the cursor a param would let this resource re-trigger itself.
            const after = untracked(() => this.store().newest);
            if (!params.query || !after) return null;

            try {
                const response: QueueUpdatesResponseDto = await this.api.getQueueUpdates(
                    params.query,
                    after,
                    abortSignal
                );
                this.pollFailures.set(0);
                return { key: params.key, items: response.items };
            } catch (error) {
                // Counted here rather than derived from error(): consecutive failures can carry an
                // identical error value, which a source-based recomputation would not see as a change.
                this.pollFailures.update(count => count + 1);
                throw error;
            }
        }
    });

    /**
     * The badge numbers, on their own resource — deliberately not in the row store, which a status or
     * window click resets. Those clicks must not touch this resource, or all nine badges blank.
     */
    protected readonly countsResource: HttpResourceRef<MonitoringCountsMap | undefined> =
        httpResource<MonitoringCountsMap>(() => {
            const query = this.countsQuery();
            if (!query) return undefined;
            return { url: this.api.countsUrl(query), method: 'GET', ...this.api.requestOptions };
        });

    protected readonly detailResource: HttpResourceRef<QueueRequestDetailDto | undefined> =
        httpResource<QueueRequestDetailDto>(() => {
            const id = this.selectedId();
            if (!id) return undefined;
            return { url: this.api.detailUrl(id), method: 'GET', ...this.api.requestOptions };
        });

    // ── Store ────────────────────────────────────────────────────────────────

    protected readonly store: WritableSignal<QueueListStore> = linkedSignal<
        { key: string; page: QueuePagePatch | null | undefined; update: QueueUpdatePatch | null | undefined },
        QueueListStore
    >({
        source: () => ({
            key: this.queryKey(),
            page: this.listResource.value(),
            update: this.updatesResource.value()
        }),
        computation: ({ key, page, update }, previous) => {
            const prior = previous?.value;

            // A patch is only usable if it was fetched FOR this key.
            if (!prior || prior.key !== key) {
                const base = emptyStore(key);
                return page && page.key === key ? applyPage(base, page) : base;
            }

            let next = prior;
            if (page && page !== prior.lastPage && page.key === key) next = applyPage(next, page);
            if (update && update !== prior.lastUpdate && update.key === key) {
                next = applyUpdate(next, update, this.queryFilters().statusGroup);
            }
            return next;
        }
    });

    //#endregion
    //#region View State

    /** Rows are already filtered by the server — this only resolves names and unpacks progress. */
    protected readonly rows = computed<readonly QueueRequestRow[]>(() => {
        const names = this.namesBySync();

        return this.store().items.map(item => {
            const resolved = names.get(item.synchronizationId);
            return {
                item,
                customerName: resolved?.customerName ?? `Kunde ${item.customerId}`,
                companyName: resolved?.companyName ?? `Regnskab ${item.companyId}`,
                synchronizationName: resolved?.synchronizationName ?? item.description,
                progress: progressOf(item)
            } satisfies QueueRequestRow;
        });
    });

    /**
     * Null means "we do not know yet", and the badges render empty rather than 0.
     *
     * Note this is `value()`, not gated on `isLoading()`. A *reload* — the ten-second poll — keeps the
     * previous value, so the badges hold steady; only a genuine key change (scope, template, search,
     * day) clears it, which is exactly when a stale number would be a lie.
     */
    protected readonly counts = computed(() => this.countsResource.value() ?? null);
    protected readonly hasMore = computed(() => this.store().hasMore);
    protected readonly searchActive = computed(() => this.queryFilters().search.trim().length > 0);

    /** The first page for the current key; a "load more" is `loadingMore` instead. */
    protected readonly loading = computed(() =>
        this.listResource.isLoading() && this.cursor() === null && this.store().items.length === 0
    );

    protected readonly loadingMore = computed(() => this.listResource.isLoading() && this.cursor() !== null);

    protected readonly listError = computed(() =>
        this.listResource.error() ? 'Kunne ikke hente kørsler.' : null
    );

    /** The cell the list is actually showing: the selected window, narrowed to the selected group. */
    private countForActiveGroup(): number {
        const window = this.countForActiveWindow();
        return window ? window[STATUS_INDEX[this.queryFilters().statusGroup]] : 0;
    }

    private countForActiveWindow(): CountsArray | null {
        const map = this.counts();
        if (!map) return null;
        switch (this.queryFilters().window) {
            case '7d': return map.sevenDays;
            case '1h': return map.oneHour;
            case 'day': return map.day;
            default: return map.twentyFourHours;
        }
    }

    //#endregion
    //#region Selection

    /**
     * What was asked for, as opposed to what is shown. Sticky across filter changes, cleared by a
     * scope change. Splitting intent from resolution is what lets the right pane render a kørsel that
     * is not in the list at all.
     */
    private readonly requestedId = linkedSignal<string, string | null>({
        source: () => this.scopeKey(),
        computation: (_scope, previous) => (previous === undefined ? this.initialQueueId : null)
    });

    protected readonly selectedId: Signal<string | null> =
        computed(() => this.requestedId() ?? this.store().items[0]?.id ?? null);

    protected readonly selectedDetail = computed(() => {
        const detail = this.detailResource.value();
        // Guard against the resource's previous value showing under a new selection.
        return detail && detail.id === this.selectedId() ? detail : null;
    });

    protected readonly selectedNames = computed(() => {
        const detail = this.selectedDetail();
        if (!detail) return { customerName: '', companyName: '', synchronizationName: '' };
        return this.namesBySync().get(detail.synchronizationId)
            ?? { customerName: '', companyName: '', synchronizationName: '' };
    });

    /**
     * The most recent poll patch, by identity.
     *
     * Read off the store rather than off the resource, so it is the patch the store actually
     * applied — and as its own computed, so it notifies only when a *new* patch arrives. Reading
     * `store().lastUpdate` inline would re-run on every page merge too.
     */
    private readonly lastUpdate = computed(() => this.store().lastUpdate);

    protected readonly detailError = computed(() => {
        const err = this.detailResource.error();
        return err ? parseHttpError(err).message : null;
    });

    //#endregion
    //#region Polling

    private readonly hasNonTerminal = computed(() => this.store().items.some(item => !isTerminal(item.status)));
    private readonly pollTick = signal(0);
    private readonly pollFailures = signal(0);
    private readonly documentVisible = signal(document.visibilityState === 'visible');

    /**
     * Backs off only when there is genuinely nothing left to watch. Decaying on "the last poll
     * returned no rows" instead would leave a kørsel stuck in Executing — the single case this page
     * exists for — polled every two minutes.
     */
    private readonly pollInterval = linkedSignal<
        { key: string; patch: QueueUpdatePatch | null | undefined },
        number
    >({
        source: () => ({ key: this.queryKey(), patch: this.updatesResource.value() }),
        computation: ({ key, patch }, previous) => {
            if (!previous || previous.source.key !== key) return POLL_MIN_MS;
            if (patch === undefined) return previous.value;
            if (patch?.items.length) return POLL_MIN_MS;
            if (untracked(() => this.hasNonTerminal())) return POLL_MIN_MS;
            return Math.min(previous.value * POLL_FACTOR, POLL_MAX_MS);
        }
    });

    private readonly pollEnabled = computed(() =>
        this.documentVisible()
        && this.serverQuery() !== null
        && this.store().newest !== null
        && this.pollFailures() < POLL_FAILURE_LIMIT
        && !this.listResource.error()
    );

    protected readonly pollingStopped = computed(() => this.pollFailures() >= POLL_FAILURE_LIMIT);

    /**
     * Both halves of a tick. The counts have their own endpoint now, so keeping the badges live is an
     * explicit second reload rather than a field on the updates payload.
     */
    private pollNow(): void {
        this.updatesResource.reload();
        this.countsResource.reload();
    }

    constructor() {
        const destroyRef = inject(DestroyRef);

        const onVisibility = () => {
            const visible = document.visibilityState === 'visible';
            this.documentVisible.set(visible);
            if (visible && this.pollEnabled()) this.pollNow();
        };
        document.addEventListener('visibilitychange', onVisibility);
        destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisibility));

        // The timer lives in an effect so its cleanup runs on every dependency change — scope, filter,
        // interval, destroy. pollTick re-arms it: a setTimeout fires once, and if the interval
        // recomputes to the same number the effect would not re-run and polling would quietly die.
        effect(onCleanup => {
            this.pollTick();
            const delay = this.pollInterval();
            this.queryKey();
            if (!this.pollEnabled()) return;

            const handle = setTimeout(() => {
                this.pollNow();
                this.pollTick.update(tick => tick + 1);
            }, delay);
            onCleanup(() => clearTimeout(handle));
        });

        // Debounce the search term into the resource keys. An effect because it is a timer, not
        // derived state — and one signal for both resources, so the rows and the badges can never be
        // a keystroke apart.
        effect(onCleanup => {
            const term = this.queryFilters().search;
            if (untracked(() => this.debouncedSearch()) === term) return;

            const handle = setTimeout(() => this.debouncedSearch.set(term), SEARCH_DEBOUNCE_MS);
            onCleanup(() => clearTimeout(handle));
        });

        // Keep the open kørsel in step with the list.
        //
        // The detail resource is keyed on the selected id alone, so the poll refreshing the rows
        // left the right pane rendering whatever snapshot it was opened with: you could watch a row
        // turn "Færdig" in the list while the pane beside it still said "Kører", until you
        // reselected it.
        //
        // Keyed on the patch, not on the row: `applyUpdate` drops a row that has left the active
        // status group, so a kørsel finishing while "Kørende" is selected disappears from the list —
        // and that is precisely when the open pane most needs the new data.
        //
        // An action rather than derived state: the id has not changed, only what it points at.
        effect(() => {
            const update = this.lastUpdate();
            if (!update) return;

            untracked(() => {
                const id = this.selectedId();
                if (id && update.items.some(item => item.id === id)) this.detailResource.reload();
            });
        });

        // A deep link to a kørsel that no longer exists: drop it and fall back to the newest row.
        //
        // An effect rather than derived state, deliberately: this is an action with two writes, and
        // folding it into `requestedId` would make that signal read the detail resource it feeds.
        effect(() => {
            const error = this.detailResource.error() as HttpErrorResponse | undefined;
            if (error?.status !== 404) return;
            untracked(() => {
                this.requestedId.set(null);
                this.anchorId.set(null);
            });
        });

        this.setupUrlSync();
    }

    /**
     * One writer for every query param. `merge` preserves the host page's own params; defaults are
     * written as null so a clean URL stays clean. `search` belongs here because the badges honour it
     * — a shared link would otherwise narrow the rows but not the counters.
     */
    private setupUrlSync(): void {
        let first = true;
        effect(() => {
            const filters = this.queryFilters();
            const queueId = this.requestedId();

            if (first) {
                // The initial state came FROM the URL; writing it straight back is noise.
                first = false;
                return;
            }

            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {
                    queueId: queueId ?? null,
                    search: filters.search.trim() || null,
                    time: filters.window === DEFAULT_QUERY_FILTERS.window ? null : filters.window,
                    day: filters.day,
                    templateId: filters.templateId,
                    status: filters.statusGroup === DEFAULT_QUERY_FILTERS.statusGroup ? null : filters.statusGroup
                },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        });
    }

    /**
     * The deep-linked kørsel, fetched on its own rather than read off `detailResource`.
     *
     * Its request depends only on the URL, which is a constant for the life of the component — so
     * this is a leaf of the signal graph. Reading `detailResource` here instead would put the window
     * hint downstream of the selection, which is downstream of the store, which is fed by the query
     * these filters build: a loop. It also means a later user click can never yank the time window,
     * because there is nothing left tying the hint to the current selection.
     *
     * Costs one duplicate GET, and only when the URL actually carries a queueId.
     */
    private readonly anchorDetailResource: HttpResourceRef<QueueRequestDetailDto | undefined> =
        httpResource<QueueRequestDetailDto>(() =>
            this.initialQueueId
                ? { url: this.api.detailUrl(this.initialQueueId), method: 'GET', ...this.api.requestOptions }
                : undefined
        );

    /**
     * The narrowest window containing the deep-linked kørsel, or null when none does.
     *
     * Null for anything older than 7 days: there is no wider window to widen to, so the list stays
     * put and the detail pane says the kørsel is outside it rather than the page silently lying.
     */
    private readonly windowHint = computed<QueueTimeWindow | null>(() => {
        const detail = this.anchorDetailResource.value();
        return detail ? narrowestWindowFor(new Date(detail.lastActivity)) : null;
    });

    /** Scroll to the deep-linked row once it is loaded. */
    protected readonly focusId = computed(() => (this.store().anchorIncluded ? this.initialQueueId : null));

    // ── Actions ──────────────────────────────────────────────────────────────

    onSelect(id: string): void {
        this.requestedId.set(id);
        this.anchorId.set(null);
    }

    onTemplateChange(templateId: string): void {
        this.queryFilters.update(filters => ({ ...filters, templateId: templateId || null }));
    }

    onLoadMore(): void {
        const items = this.store().items;
        if (items.length === 0) return;
        this.cursor.set(items[items.length - 1].lastActivity);
    }

    reload(): void {
        this.cursor.set(null);
        this.listResource.reload();
        // The manual refresh means "show me what is true now", which includes the open pane. The
        // poll reaches it through the update patch; this path has no patch to go through.
        this.detailResource.reload();
    }

    resumePolling(): void {
        this.pollFailures.set(0);
        this.updatesResource.reload();
    }

    async onDownloadPayload(executer: QueueExecuterDto): Promise<void> {
        // Named from the kørsel, not the chunk: the accordion only knows its own delkørsel, and
        // tenant, regnskab, skabelon and task all live on the request.
        const detail = this.selectedDetail();
        if (!detail) return;
        try {
            await this.api.downloadPayload(detail.id, executer.id, payloadFileName(detail, executer));
        } catch {
            this.toastr.error('Kunne ikke hente payload for delkørslen.');
        }
    }

    async copyLink(): Promise<void> {
        try {
            await navigator.clipboard.writeText(window.location.href);
            this.toastr.success('Link kopieret.');
        } catch {
            this.toastr.error('Kunne ikke kopiere linket.');
        }
    }
}

function applyPage(store: QueueListStore, patch: QueuePagePatch): QueueListStore {
    const items = patch.append
        ? dedupe([...store.items, ...patch.items])
        : [...patch.items].sort(byActivityDesc);

    return {
        ...store,
        items,
        hasMore: patch.hasMore,
        newest: newestOf(items),
        anchorIncluded: patch.append ? store.anchorIncluded : patch.anchorIncluded,
        lastPage: patch
    };
}

/**
 * Merge changed rows in place, prepend genuinely new ones, and drop anything that has left the
 * active status group.
 *
 * The drop is why /updates ignores the group filter: if the server withheld a row that moved out of
 * "running", the client would keep rendering its stale status forever.
 */
function applyUpdate(store: QueueListStore, patch: QueueUpdatePatch, statusGroup: QueueStatusGroup): QueueListStore {
    const byId = new Map(store.items.map(item => [item.id, item] as const));
    for (const item of patch.items) byId.set(item.id, item);

    const items = [...byId.values()]
        .filter(item => statusGroup === 'all' || item.statusGroup === statusGroup)
        .sort(byActivityDesc);

    return {
        ...store,
        items,
        newest: newestOf(items) ?? store.newest,
        lastUpdate: patch
    };
}

function dedupe(items: readonly QueueRequestListItemDto[]): QueueRequestListItemDto[] {
    const byId = new Map(items.map(item => [item.id, item] as const));
    return [...byId.values()].sort(byActivityDesc);
}
